/**
 * Owner: Masa Bayu
 * File: text-authoring-normalize.ts
 * Date:Time: 2026-09-20 22:14
 * Description: Phase 2 CA-03 N1–N6 deterministic text-content normalization.
 */
import { isQXTextMarkType, QX_TEXT_MARK_TYPES } from './text-mark';
import { QX_TEXT_BLOCK_TYPES, QX_TEXT_INLINE_TYPES, isQXTextLeafBlockType, } from './text-model';
const BLOCK_TYPES = new Set(QX_TEXT_BLOCK_TYPES);
const INLINE_TYPES = new Set(QX_TEXT_INLINE_TYPES);
function fail(code, path, message) {
    return { ok: false, errors: [{ code, path, message }] };
}
/** Canonical mark identity: dedupe + registry order (CA-05 RC3 · insertion-order independent). */
export function canonicalizeQXTextMarks(marks) {
    const seen = new Set();
    const ordered = [];
    for (const registryType of QX_TEXT_MARK_TYPES) {
        for (const mark of marks) {
            if (mark.type !== registryType || seen.has(mark.type)) {
                continue;
            }
            if (!isQXTextMarkType(mark.type)) {
                continue;
            }
            seen.add(mark.type);
            ordered.push(mark.type === 'text-color' || mark.type === 'highlight'
                ? { type: mark.type, value: mark.value }
                : { type: mark.type });
        }
    }
    return ordered;
}
function marksKey(marks) {
    return canonicalizeQXTextMarks(marks)
        .map((mark) => mark.type === 'text-color' || mark.type === 'highlight' ? `${mark.type}=${mark.value}` : mark.type)
        .join('|');
}
function sameMarkSet(left, right) {
    return marksKey(left) === marksKey(right);
}
/** N2 — merge adjacent text inlines with identical marks (left-to-right). */
export function mergeAdjacentIdenticalMarkText(inlines) {
    const out = [];
    for (const inline of inlines) {
        if (inline.type !== 'text') {
            out.push(inline);
            continue;
        }
        const marks = canonicalizeQXTextMarks(inline.marks);
        const prev = out[out.length - 1];
        const typography = inline.typography;
        if (prev?.type === 'text' &&
            sameMarkSet(prev.marks, marks) &&
            JSON.stringify(prev.typography ?? null) === JSON.stringify(typography ?? null)) {
            out[out.length - 1] = {
                type: 'text',
                text: prev.text + inline.text,
                marks,
                ...(typography !== undefined ? { typography } : {}),
            };
            continue;
        }
        out.push({
            type: 'text',
            text: inline.text,
            marks,
            ...(typography !== undefined ? { typography } : {}),
        });
    }
    return out;
}
function normalizeInline(inline, path) {
    if (!INLINE_TYPES.has(inline.type)) {
        return {
            ok: false,
            result: fail('n3-unknown-type', path, `unknown inline type "${inline.type}" (N3 fail-closed).`),
        };
    }
    if (inline.type === 'text') {
        return {
            ok: true,
            inline: {
                type: 'text',
                text: inline.text,
                marks: canonicalizeQXTextMarks(inline.marks),
                ...(inline.typography !== undefined ? { typography: inline.typography } : {}),
            },
        };
    }
    if (inline.type === 'link') {
        return {
            ok: true,
            inline: {
                ...inline,
                marks: canonicalizeQXTextMarks(inline.marks),
            },
        };
    }
    // note-ref: identity only — preserve targetRef (N5).
    return { ok: true, inline: { type: 'note-ref', targetRef: inline.targetRef } };
}
function normalizeInlines(inlines, path) {
    const next = [];
    for (let index = 0; index < inlines.length; index += 1) {
        const current = inlines[index];
        if (current === undefined) {
            continue;
        }
        const normalized = normalizeInline(current, `${path}[${String(index)}]`);
        if (!normalized.ok) {
            return normalized;
        }
        next.push(normalized.inline);
    }
    return { ok: true, inlines: mergeAdjacentIdenticalMarkText(next) };
}
function normalizeCaption(caption, path) {
    const inlines = normalizeInlines(caption.inlines, `${path}.inlines`);
    if (!inlines.ok) {
        return inlines;
    }
    return {
        ok: true,
        caption: caption.language
            ? { type: 'caption', inlines: inlines.inlines, language: caption.language }
            : { type: 'caption', inlines: inlines.inlines },
    };
}
function normalizeFigure(figure, path) {
    let caption;
    if (figure.caption !== undefined) {
        const normalized = normalizeCaption(figure.caption, `${path}.caption`);
        if (!normalized.ok) {
            return normalized;
        }
        caption = normalized.caption;
    }
    return {
        ok: true,
        figure: {
            type: 'figure',
            ...(figure.assetRef !== undefined ? { assetRef: { id: figure.assetRef.id } } : {}),
            ...(figure.alt !== undefined ? { alt: figure.alt } : {}),
            ...(caption !== undefined ? { caption } : {}),
            ...(figure.language !== undefined ? { language: figure.language } : {}),
        },
    };
}
function normalizeList(list, path) {
    // N1 — empty list is ambiguous illegal shell.
    if (list.items.length < 1) {
        return {
            ok: false,
            result: fail('n1-ambiguous-tree', path, 'list without items is an ambiguous illegal shell (N1).'),
        };
    }
    const items = [];
    for (let index = 0; index < list.items.length; index += 1) {
        const item = list.items[index];
        if (item === undefined || item.type !== 'list-item') {
            return {
                ok: false,
                result: fail('n3-unknown-type', `${path}.items[${String(index)}]`, 'list children must be list-item (N3).'),
            };
        }
        const inlines = normalizeInlines(item.inlines, `${path}.items[${String(index)}].inlines`);
        if (!inlines.ok) {
            return inlines;
        }
        let nested;
        if (item.list !== undefined) {
            const nestedResult = normalizeList(item.list, `${path}.items[${String(index)}].list`);
            if (!nestedResult.ok) {
                return nestedResult;
            }
            nested = nestedResult.list;
        }
        items.push(item.language
            ? {
                type: 'list-item',
                inlines: inlines.inlines,
                ...(nested !== undefined ? { list: nested } : {}),
                language: item.language,
            }
            : {
                type: 'list-item',
                inlines: inlines.inlines,
                ...(nested !== undefined ? { list: nested } : {}),
            });
    }
    return {
        ok: true,
        list: list.language
            ? { type: 'list', items, language: list.language }
            : { type: 'list', items },
    };
}
function normalizeLeaf(block, path) {
    const inlines = normalizeInlines(block.inlines, `${path}.inlines`);
    if (!inlines.ok) {
        return inlines;
    }
    if (block.type === 'heading') {
        return {
            ok: true,
            block: block.language
                ? { type: 'heading', level: block.level, inlines: inlines.inlines, language: block.language }
                : { type: 'heading', level: block.level, inlines: inlines.inlines },
        };
    }
    if (block.type === 'paragraph') {
        return {
            ok: true,
            block: {
                type: 'paragraph',
                inlines: inlines.inlines,
                ...(block.format !== undefined ? { format: block.format } : {}),
                ...(block.language !== undefined ? { language: block.language } : {}),
            },
        };
    }
    if (block.type === 'footnote' || block.type === 'endnote') {
        // N5 — preserve note id; never re-id on tidy.
        return {
            ok: true,
            block: block.language
                ? {
                    type: block.type,
                    id: block.id,
                    inlines: inlines.inlines,
                    language: block.language,
                }
                : { type: block.type, id: block.id, inlines: inlines.inlines },
        };
    }
    return {
        ok: true,
        block: block.language
            ? { type: block.type, inlines: inlines.inlines, language: block.language }
            : { type: block.type, inlines: inlines.inlines },
    };
}
function normalizeBlock(block, path) {
    if (!BLOCK_TYPES.has(block.type)) {
        return {
            ok: false,
            result: fail('n3-unknown-type', path, `unknown block type "${block.type}" (N3 fail-closed).`),
        };
    }
    if (block.type === 'list') {
        const list = normalizeList(block, path);
        if (!list.ok) {
            return list;
        }
        return { ok: true, block: list.list };
    }
    if (block.type === 'rule') {
        return { ok: true, block: { type: 'rule', color: block.color } };
    }
    if (block.type === 'figure') {
        const figure = normalizeFigure(block, path);
        if (!figure.ok) {
            return figure;
        }
        return { ok: true, block: figure.figure };
    }
    if (isQXTextLeafBlockType(block.type)) {
        return normalizeLeaf(block, path);
    }
    return {
        ok: false,
        result: fail('normalize-rejected', path, 'unsupported block for normalize.'),
    };
}
/**
 * Deterministic N1–N6 normalize for Authoring/QXW text content.
 * Does not invent PSA structure (N6). Does not admit bare text (N4 = adapter only).
 */
export function normalizeQXTextContent(content) {
    // N4 — bare string / non-object is adapter-only; never invent paragraphs here.
    if (typeof content === 'string') {
        return fail('n4-bare-text-forbidden', 'content', 'bare text admitted only at paste/import adapter boundary (N4).');
    }
    const blocks = [];
    for (let index = 0; index < content.blocks.length; index += 1) {
        const block = content.blocks[index];
        if (block === undefined) {
            continue;
        }
        const normalized = normalizeBlock(block, `blocks[${String(index)}]`);
        if (!normalized.ok) {
            return normalized.result;
        }
        blocks.push(normalized.block);
    }
    return {
        ok: true,
        content: content.language !== undefined
            ? { blocks, language: content.language }
            : { blocks },
    };
}
/**
 * N4 adapter-boundary helper — wrap bare text as a single paragraph.
 * Must not be used to rewrite already-valid QXW trees.
 */
export function wrapBareTextAsParagraph(text) {
    const inline = { type: 'text', text, marks: [] };
    return { blocks: [{ type: 'paragraph', inlines: [inline] }] };
}
/** Prove two contents normalize to identical canonical output. */
export function authoringNormalizeEquals(left, right) {
    const a = normalizeQXTextContent(left);
    const b = normalizeQXTextContent(right);
    if (!a.ok || !b.ok) {
        return false;
    }
    return JSON.stringify(a.content) === JSON.stringify(b.content);
}
//# sourceMappingURL=text-authoring-normalize.js.map